import { jobIsEditable } from '@helpers/jobStatus/jobIsEditable';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { routes } from '@routes';
import { JobResponseDto } from 'app/API';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useState } from 'react';
import { InlineEditRow } from './InlineRow';

type TProps = {
  reload: () => void;
  search: string;
  jobs: JobResponseDto[];
  checkedJobs: string[];
  setCheckedJobs: (v: string[]) => void;
};

export const useJobsTableHead = ({ reload, jobs, checkedJobs, setCheckedJobs }: TProps) => {
  const { workspace, me, organization } = useAuthContext();
  const { openModal, closeModal } = useModalContext();

  const [hiddenButtonsVisible, setHiddenButtonsVisible] = useState<string | null>(null);
  const isVsAdmin = me?.roles?.includes('ROLE_VS_ADMIN');

  const canViewAllJobsHistory = organization?.permissions?.viewJobs === 'all';
  const canManageWorkspaces = !!organization?.permissions?.manageWorkspaces;
  const canSubmitJobs = !!organization?.permissions?.submitJobs;
  const jobsAvailableToEdit = jobs.filter((job) => !job.asOneLocked);

  const checkJob = (id: string) => {
    if (checkedJobs.includes(id)) {
      setCheckedJobs(checkedJobs.filter((jobId) => jobId !== id));
    } else {
      setCheckedJobs([...checkedJobs, id]);
    }
  };

  const checkAllJobs = () => {
    if (checkedJobs.length === jobsAvailableToEdit.length) {
      setCheckedJobs([]);
    } else {
      setCheckedJobs(jobsAvailableToEdit.map((job) => job.id));
    }
  };

  const showMoveJobToWSModal = (job: JobResponseDto) => {
    openModal(ModalNamesEnum.MoveJobToWSModal, {
      onSuccess: () => {
        reload();
        closeModal();
      },
      job,
    });
  };

  const editAssignee = (job: JobResponseDto) => {
    openModal(ModalNamesEnum.AssignJobModal, {
      onSuccess: () => {
        reload();
        closeModal();
      },
      job,
    });
  };

  const editDeadline = (job: JobResponseDto) => {
    openModal(ModalNamesEnum.EditDeadline, {
      onSuccess: () => {
        reload();
        closeModal();
      },
      job,
    });
  };

  const handleShowHistory = (job: JobResponseDto) => {
    openModal(ModalNamesEnum.JobHistory, {
      jobId: job.id,
    });
  };

  const generateJobLink = (jobId: string, jobStatus: string) => {
    if (jobIsEditable(jobStatus)) {
      return routes.jobEdit.make(organization?.slug ?? '', workspace?.slug ?? '', jobId);
    }

    return routes.jobDetails.make(organization?.slug ?? '', workspace?.slug ?? '', jobId);
  };

  const renderInlineEditRow = (data: JobResponseDto) => {
    return <InlineEditRow data={data} />;
  };

  return {
    checkJob,
    checkAllJobs,
    showMoveJobToWSModal,
    editAssignee,
    editDeadline,
    handleShowHistory,
    generateJobLink,
    renderInlineEditRow,
    jobsAvailableToEdit,
    hiddenButtonsVisible,
    setHiddenButtonsVisible,
    isVsAdmin,
    canViewAllJobsHistory,
    canManageWorkspaces,
    canSubmitJobs,
    workspace,
    me,
    organization,
    openModal,
    closeModal,
  };
};
